import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Play, MapPin, Calendar, Clock, Mic2, Trophy, Users, Camera,
  Sparkles, Music, Star, ArrowRight, Radio, Ticket, PartyPopper, Loader2
} from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gala = () => {
  const [gala, setGala] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageVisibility('page_gala_enabled');
  
  // Fetch gala data
  useEffect(() => {
    const fetchGala = async () => {
      try {
        const res = await axios.get(`${API}/gala`);
        setGala(res.data);
      } catch (error) {
        console.error('Error fetching gala:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGala();
  }, []);

  // Countdown
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const galaDate = gala?.date ? new Date(gala.date) : new Date('2026-05-15T20:00:00');
    const interval = setInterval(() => {
      const now = new Date();
      const diff = galaDate - now;
      
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [gala?.date]);

  // Default program if none exists
  const defaultProgram = [
    { time: '19:00', title: 'Ouverture des portes', description: 'Accueil des invités, cocktail de bienvenue' },
    { time: '20:00', title: 'Cérémonie d\'ouverture', description: 'Performance musicale d\'ouverture' },
    { time: '20:30', title: 'Premières récompenses', description: 'Meilleur jeune espoir, Compositeur de l\'année' },
    { time: '21:15', title: 'Showcase acoustique', description: 'Performance live d\'un artiste nominé' },
    { time: '21:45', title: 'Récompenses suite', description: 'Festival de l\'année, Album de l\'année' },
    { time: '22:30', title: 'Grand moment', description: 'Titre de l\'année, Artiste de l\'année' },
    { time: '23:00', title: 'Concert de clôture', description: 'Performance du lauréat Artiste de l\'année' },
    { time: '00:00', title: 'Fest-noz de fin', description: 'Danse traditionnelle pour tous' },
  ];

  const programItems = gala?.program?.length > 0 ? gala.program : defaultProgram;
  const location = gala?.location || 'Lannion, Côtes-d\'Armor, Bretagne';
  const description = gala?.description || 'L\'aboutissement émotionnel d\'une année de célébration';

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian" data-testid="gala-page">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/6532369/pexels-photo-6532369.jpeg?auto=compress&cs=tinysrgb&w=1920)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/60" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pt-32">
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 border border-gold/30 bg-gold/10 backdrop-blur-sm">
            <Calendar className="w-4 h-4 text-gold" />
            <span className="text-sm uppercase tracking-widest text-gold">15 Mai 2026</span>
          </div>
          
          <h1 className="font-syne text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter uppercase text-white mb-6">
            LA SOIRÉE<br />
            <span className="text-gold-gradient">DE GALA</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/70 max-w-2xl mx-auto mb-4">
            {description}
          </p>
          
          <div className="flex items-center justify-center gap-2 text-white/50 mb-12">
            <MapPin className="w-5 h-5 text-gold" />
            <span className="text-lg">{location}</span>
          </div>
          
          {/* Video Play Button */}
          <div className="mb-16">
            <button className="group relative">
              <div className="w-24 h-24 rounded-full bg-gold/20 backdrop-blur-sm flex items-center justify-center border border-gold/50 group-hover:bg-gold/30 transition-all group-hover:scale-110">
                <Play className="w-10 h-10 text-gold ml-1" />
              </div>
              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-sm text-white/50 whitespace-nowrap">
                Voir le teaser
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Countdown */}
      <section className="py-16 bg-obsidian-light border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <span className="text-gold text-sm uppercase tracking-widest">Compte à rebours</span>
          </div>
          
          <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
            {[
              { value: countdown.days, label: 'Jours' },
              { value: countdown.hours, label: 'Heures' },
              { value: countdown.minutes, label: 'Minutes' },
              { value: countdown.seconds, label: 'Secondes' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 border border-white/10">
                <div className="font-syne text-4xl md:text-6xl font-bold text-gold mb-2">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-xs uppercase tracking-wider text-white/50">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold text-sm uppercase tracking-widest">L'Expérience</span>
            <h2 className="font-syne text-3xl md:text-5xl font-bold tracking-tight text-white mt-4 mb-4">
              Un Moment Iconique
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Plus qu'une cérémonie, une véritable célébration de la culture musicale bretonne
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Mic2, title: 'Performances Live', desc: 'Artistes nominés et invités surprise sur scène' },
              { icon: Trophy, title: 'Remise des Prix', desc: 'Cérémonie scénarisée et émotions garanties' },
              { icon: Users, title: 'Networking', desc: 'Rencontres entre artistes, professionnels et passionnés' },
              { icon: Camera, title: 'Captation', desc: 'Diffusion live et archive vidéo de la soirée' },
            ].map((item, idx) => (
              <div key={idx} className="p-8 border border-white/10 bg-obsidian-light hover:border-gold/50 transition-all group">
                <item.icon className="w-10 h-10 text-gold mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="font-syne text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme */}
      <section className="py-24 bg-obsidian-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold text-sm uppercase tracking-widest">Déroulé</span>
            <h2 className="font-syne text-3xl md:text-5xl font-bold tracking-tight text-white mt-4">
              Programme de la Soirée
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[60px] top-0 bottom-0 w-px bg-gradient-to-b from-gold via-electric-blue to-gold/30 hidden md:block" />
              
              <div className="space-y-6">
                {programItems.map((item, idx) => (
                  <div key={idx} className="flex gap-6 group">
                    <div className="w-[60px] flex-shrink-0 text-right">
                      <span className="font-mono text-gold font-bold">{item.time}</span>
                    </div>
                    <div className="relative">
                      <div className="absolute left-0 top-2 w-3 h-3 rounded-full bg-gold border-2 border-obsidian-light hidden md:block" />
                      <div className="md:pl-8 p-4 border border-white/10 bg-obsidian hover:border-gold/30 transition-colors">
                        <h3 className="font-syne font-bold text-white mb-1">{item.title}</h3>
                        <p className="text-sm text-white/50">{item.description || item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue */}
      <section className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-gold text-sm uppercase tracking-widest">Le Lieu</span>
              <h2 className="font-syne text-3xl md:text-4xl font-bold tracking-tight text-white mt-4 mb-6">
                Lannion, Cœur du Trégor
              </h2>
              <p className="text-white/60 mb-6">
                La soirée de gala se tiendra dans un lieu d'exception au cœur de Lannion, ville historique du Trégor. Un cadre prestigieux pour une soirée mémorable.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Capacité d\'accueil premium',
                  'Acoustique optimale pour les performances',
                  'Espace networking et cocktail',
                  'Accessibilité et stationnement facilités'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white/80">
                    <Star className="w-4 h-4 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-4">
                <MapPin className="w-6 h-6 text-gold" />
                <div>
                  <p className="text-white font-medium">Lannion</p>
                  <p className="text-sm text-white/50">Côtes-d'Armor, Bretagne</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] bg-obsidian-light border border-white/10 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1590080875515-8a3a8dc5735e?w=800" 
                  alt="Lannion"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diffusion */}
      <section className="py-24 bg-obsidian-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-electric-cyan text-sm uppercase tracking-widest">Ne manquez rien</span>
          <h2 className="font-syne text-3xl md:text-4xl font-bold tracking-tight text-white mt-4 mb-6">
            Diffusion en Direct
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-12">
            Impossible d'être présent physiquement ? La cérémonie sera diffusée en direct sur nos plateformes. Vivez l'émotion depuis chez vous.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { icon: Radio, label: 'Streaming live' },
              { icon: Camera, label: 'Multi-caméras' },
              { icon: PartyPopper, label: 'Replay disponible' },
            ].map((item, idx) => (
              <div key={idx} className="p-6 border border-white/10">
                <item.icon className="w-8 h-8 text-electric-cyan mx-auto mb-3" />
                <p className="text-white">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1741356474365-5f0041f89eaa?w=1920)'
          }}
        />
        <div className="absolute inset-0 bg-obsidian/80" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Participez à l'Histoire
          </h2>
          <p className="text-xl text-white/60 mb-10 max-w-xl mx-auto">
            Proposez vos artistes, votez pour vos favoris et rejoignez-nous pour la grande soirée.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/nominations"
              className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-10 py-5 text-sm transition-all hover:scale-105 inline-flex items-center gap-3"
            >
              Proposer un artiste
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/auth"
              className="border border-white/20 hover:border-white text-white px-10 py-5 uppercase tracking-wider text-sm hover:bg-white/5 transition-all"
            >
              Créer un compte
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Gala;
